/**
 * @voltxampere/ui — Parameter schemas (Sprint 14)
 *
 * Per-component-type form-field definitions. Inspector renders
 * these as typed inputs (number+unit picker, toggle, select,
 * text). Each `ParamField`'s SI base unit + prefix list comes
 * from Blueprint §10's standard ranges; the inspector keeps the
 * canonical SI value in `params[key]` regardless of which prefix
 * the user picked from the dropdown — display only.
 *
 * Multi-select inspector mode calls `getCommonParamFields(types)`
 * to compute the field intersection; if every selected component
 * shares (resistor + resistor → both have `resistance`), they
 * appear; otherwise the inspector renders the "Karışık seçim"
 * empty state (no fields).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const RESISTANCE = {
    key: "resistance",
    label: "inspector.param.resistance",
    type: "number",
    unit: "Ω",
    siPrefixes: ["m", "k", "M"],
    min: 0.001,
    max: 1e9,
};
const CAPACITANCE = {
    key: "capacitance",
    label: "inspector.param.capacitance",
    type: "number",
    unit: "F",
    siPrefixes: ["p", "n", "μ", "m"],
    min: 1e-15,
    max: 1,
};
const INDUCTANCE = {
    key: "inductance",
    label: "inspector.param.inductance",
    type: "number",
    unit: "H",
    siPrefixes: ["n", "μ", "m"],
    min: 1e-12,
    max: 1,
};
const VOLTAGE = {
    key: "voltage",
    label: "inspector.param.voltage",
    type: "number",
    unit: "V",
    siPrefixes: ["m", "k"],
    min: -1000,
    max: 1000,
};
const FREQUENCY = {
    key: "frequency",
    label: "inspector.param.frequency",
    type: "number",
    unit: "Hz",
    siPrefixes: ["m", "k", "M", "G"],
    min: 1e-3,
    max: 1e9,
};
const PHASE = {
    key: "phase",
    label: "inspector.param.phase",
    type: "number",
    unit: "°",
    min: -360,
    max: 360,
};
const AMPLITUDE = {
    key: "amplitude",
    label: "inspector.param.amplitude",
    type: "number",
    unit: "V",
    siPrefixes: ["m", "k"],
    min: 0,
    max: 1000,
};
const TOLERANCE = {
    key: "tolerance",
    label: "inspector.param.tolerance",
    type: "number",
    unit: "%",
    min: 0,
    max: 100,
};
const POWER_RATING = {
    key: "powerRating",
    label: "inspector.param.powerRating",
    type: "number",
    unit: "W",
    siPrefixes: ["m"],
    min: 0,
    max: 1000,
};
const SATURATION_CURRENT = {
    key: "saturationCurrent",
    label: "inspector.param.saturationCurrent",
    type: "number",
    unit: "A",
    siPrefixes: ["p", "n", "μ", "m"],
    min: 1e-18,
    max: 1,
};
// Sprint 24 A0 (bug_010 fix) — pre-existing Sprint 14 mismatch:
// inspector key was `"emissionCoefficient"` but every engine schema
// (diode/led/zener/bjt) reads `params.ideality` via `getNumericParam`.
// User edits silently dropped. Sprint 23 LED/Zener dedicated promotion
// amplified the impact (LED-specific n=3 default became advertised as
// tunable but UI writes never reached the engine). Display label
// keeps the user-facing "Emission coefficient" string for backwards
// compatibility — only the params lookup key migrates.
const EMISSION_COEFF = {
    key: "ideality",
    label: "inspector.param.emissionCoefficient",
    type: "number",
    min: 1,
    max: 4,
};
const FORWARD_VOLTAGE = {
    key: "forwardVoltage",
    label: "inspector.param.forwardVoltage",
    type: "number",
    unit: "V",
    siPrefixes: ["m"],
    min: 0,
    max: 5,
};
const ZENER_VOLTAGE = {
    key: "zenerVoltage",
    label: "inspector.param.zenerVoltage",
    type: "number",
    unit: "V",
    min: 0.5,
    max: 200,
};
const BJT_BETA = {
    key: "beta",
    label: "inspector.param.beta",
    type: "number",
    min: 1,
    max: 1000,
};
const MOSFET_KP = {
    key: "kp",
    label: "inspector.param.kp",
    type: "number",
    unit: "A/V²",
    siPrefixes: ["μ", "m"],
    min: 1e-9,
    max: 1,
};
const MOSFET_VT = {
    key: "thresholdVoltage",
    label: "inspector.param.thresholdVoltage",
    type: "number",
    unit: "V",
    min: -10,
    max: 10,
};
const MOSFET_W = {
    key: "channelWidth",
    label: "inspector.param.channelWidth",
    type: "number",
    unit: "m",
    siPrefixes: ["n", "μ"],
    min: 1e-9,
    max: 1e-3,
};
const MOSFET_L = {
    key: "channelLength",
    label: "inspector.param.channelLength",
    type: "number",
    unit: "m",
    siPrefixes: ["n", "μ"],
    min: 1e-9,
    max: 1e-3,
};
const JFET_IDSS = {
    key: "idss",
    label: "inspector.param.idss",
    type: "number",
    unit: "A",
    siPrefixes: ["μ", "m"],
    min: 1e-9,
    max: 1,
};
const JFET_VP = {
    key: "pinchOff",
    label: "inspector.param.pinchOff",
    type: "number",
    unit: "V",
    min: -10,
    max: 0,
};
const OPAMP_GAIN = {
    key: "openLoopGain",
    label: "inspector.param.openLoopGain",
    type: "number",
    min: 1e3,
    max: 1e9,
};
const OPAMP_VSAT = {
    key: "saturationVoltage",
    label: "inspector.param.saturationVoltage",
    type: "number",
    unit: "V",
    min: 1,
    max: 30,
};
const SWITCH_STATE = {
    key: "closed",
    label: "inspector.param.closed",
    type: "boolean",
};
// Sprint 23 B4 — Potentiometer position now uses the canonical
// engine schema's `position` key, rendered as a range slider in the
// inspector (Şefim's UI request — drag interactivity for circuit
// design exploration).
const POT_POSITION = {
    key: "position",
    label: "inspector.param.position",
    type: "range",
    min: 0.001,
    max: 0.999,
    step: 0.001,
};
// Sprint 23 B3 — JFET Shichman-Hodges parameters (replaces the
// pre-Sprint-23 JFET_IDSS / JFET_VP placeholders).
const JFET_BETA = {
    key: "Beta",
    label: "inspector.param.beta",
    type: "number",
    unit: "A/V²",
    siPrefixes: ["μ", "m"],
    min: 1e-9,
    max: 1,
};
const JFET_VT0 = {
    key: "Vt0",
    label: "inspector.param.thresholdVoltage",
    type: "number",
    unit: "V",
    min: -10,
    max: 10,
};
const JFET_LAMBDA = {
    key: "Lambda",
    label: "inspector.param.channelMod",
    type: "number",
    unit: "1/V",
    min: 0,
    max: 1,
};
const SOURCE_KIND = {
    key: "sourceKind",
    label: "inspector.param.sourceKind",
    type: "enum",
    options: [
        { value: "voltage", label: "Voltage" },
        { value: "current", label: "Current" },
    ],
};
export const PARAM_SCHEMAS = Object.freeze({
    resistor: [RESISTANCE, TOLERANCE, POWER_RATING],
    capacitor: [CAPACITANCE, TOLERANCE],
    inductor: [INDUCTANCE, TOLERANCE],
    potentiometer: [RESISTANCE, POT_POSITION],
    switch: [SWITCH_STATE],
    ground: [],
    "dc-source": [VOLTAGE, SOURCE_KIND],
    "ac-source": [AMPLITUDE, FREQUENCY, PHASE],
    "pulse-source": [AMPLITUDE, FREQUENCY],
    diode: [SATURATION_CURRENT, EMISSION_COEFF, FORWARD_VOLTAGE],
    led: [SATURATION_CURRENT, EMISSION_COEFF, FORWARD_VOLTAGE],
    zener: [SATURATION_CURRENT, EMISSION_COEFF, ZENER_VOLTAGE],
    npn: [SATURATION_CURRENT, BJT_BETA, EMISSION_COEFF],
    pnp: [SATURATION_CURRENT, BJT_BETA, EMISSION_COEFF],
    nmos: [MOSFET_KP, MOSFET_VT, MOSFET_W, MOSFET_L],
    pmos: [MOSFET_KP, MOSFET_VT, MOSFET_W, MOSFET_L],
    jfet: [JFET_IDSS, JFET_VP],
    // Sprint 23 B3 — JFET Shichman-Hodges canonical params for the new
    // dedicated `jfet-n` / `jfet-p` engine stamps.
    "jfet-n": [JFET_BETA, JFET_VT0, JFET_LAMBDA],
    "jfet-p": [JFET_BETA, JFET_VT0, JFET_LAMBDA],
    "op-amp": [OPAMP_GAIN, OPAMP_VSAT],
});
const EMPTY = Object.freeze([]);
export function getParamFields(componentType) {
    return PARAM_SCHEMAS[componentType] ?? EMPTY;
}
/**
 * Intersection of param-field arrays across the supplied
 * component types — multi-select inspector mode shows only the
 * fields every selected component understands. Equality is by
 * `key`; if a field's other attributes differ across types
 * (rare today) the FIRST type's definition wins so the
 * intersection stays a flat list.
 */
export function getCommonParamFields(componentTypes) {
    if (componentTypes.length === 0)
        return EMPTY;
    const first = getParamFields(componentTypes[0] ?? "");
    if (componentTypes.length === 1)
        return first;
    const otherKeys = componentTypes
        .slice(1)
        .map((t) => new Set(getParamFields(t).map((f) => f.key)));
    return first.filter((field) => otherKeys.every((set) => set.has(field.key)));
}
//# sourceMappingURL=param-schema.js.map